import mapboxgl from 'mapbox-gl';
import { environment } from '../../../environments/environment';
import { ExportPin } from '../../utilities/reportExport';
import { exportMapView } from './export-map-view';

/**
 * The size of the picture. The cover page of the report is landscape and prints the map in a box 720 pt wide and 428
 * pt tall (hero-api: ReportExportLimits.COVER_MAP_WIDTH_PT and COVER_MAP_HEIGHT_PT), so the picture has the same
 * proportions, at two pixels to the point, and fills the box. Every pixel is half a point: pins and labels are sized
 * for that (a 20 px label prints at 10 pt), and it is still sharp at 144 dpi.
 */
const IMAGE_WIDTH = 1440;
const IMAGE_HEIGHT = 856;

/**
 * The picture is sent as a JPEG: satellite imagery is photographs, so a PNG of it is about eight times as big (3 MB
 * against 0.4 MB for the same map), and the API accepts a map of about 9 MB. 0.9 keeps the pins and their labels crisp.
 */
const JPEG_QUALITY = 0.9;

/** The imagery has to be credited where it is shown, and on paper that is on the picture. */
const ATTRIBUTION = '© Mapbox © Maxar';

const MAP_STYLE = 'mapbox://styles/mapbox/satellite-v9';
const LOAD_TIMEOUT_MS = 30_000;

/**
 * Draws the map of an export report: a pin for every report, labeled with the report's id, zoomed to fit them all
 * (as close as the pins allow, so that reports a few meters apart are not drawn on top of each other).
 * The map is drawn off screen (it never appears on the page) and returned as a JPEG data URL.
 *
 * @throws Error if the map cannot be drawn (for example, it does not load in time)
 */
export async function renderExportMapImage(pins: ExportPin[]): Promise<string> {
  const container = document.createElement('div');
  container.style.cssText = `position:fixed;left:-10000px;top:0;width:${IMAGE_WIDTH}px;height:${IMAGE_HEIGHT}px;`;
  document.body.appendChild(container);

  let map: mapboxgl.Map | null = null;

  try {
    const view = exportMapView(pins);

    map = new mapboxgl.Map({
      accessToken: environment.mapboxAccessToken,
      container,
      style: MAP_STYLE,
      ...(view.kind === 'bounds'
        ? { bounds: view.bounds, fitBoundsOptions: { padding: view.padding, maxZoom: view.maxZoom } }
        : { center: view.center, zoom: view.zoom }),
      interactive: false,
      attributionControl: false,
      fadeDuration: 0,
      // Without this the picture cannot be read back from the canvas.
      preserveDrawingBuffer: true,
    });

    await whenReady(map);
    addPins(map, pins);
    await whenIdle(map);

    return toDataUrl(map.getCanvas());
  } finally {
    map?.remove();
    container.remove();
  }
}

function addPins(map: mapboxgl.Map, pins: ExportPin[]): void {
  map.addSource('export-pins', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: pins.map((pin) => ({
        type: 'Feature',
        properties: { label: pin.label, color: pin.color },
        geometry: { type: 'Point', coordinates: [pin.longitude, pin.latitude] },
      })),
    },
  });

  map.addLayer({
    id: 'export-pins-circle',
    type: 'circle',
    source: 'export-pins',
    paint: {
      'circle-radius': 12,
      'circle-color': ['get', 'color'],
      'circle-stroke-width': 3,
      'circle-stroke-color': '#FFFFFF',
    },
  });

  map.addLayer({
    id: 'export-pins-label',
    type: 'symbol',
    source: 'export-pins',
    layout: {
      'text-field': ['get', 'label'],
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-size': 20,
      'text-anchor': 'top',
      'text-offset': [0, 0.8],
      // Every report has to be labeled, even where pins are close together.
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#1A1E25',
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 2.5,
    },
  });
}

function whenReady(map: mapboxgl.Map): Promise<void> {
  return waitFor(map, 'load', () => map.loaded() && map.isStyleLoaded());
}

function whenIdle(map: mapboxgl.Map): Promise<void> {
  return waitFor(map, 'idle', () => false);
}

/** Resolves on the event (or at once if `alreadyDone`), rejects on a map error or if it takes too long. */
function waitFor(map: mapboxgl.Map, event: 'load' | 'idle', alreadyDone: () => boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    if (alreadyDone()) {
      resolve();
      return;
    }

    const timer = setTimeout(() => finish(new Error('The map did not load in time')), LOAD_TIMEOUT_MS);

    const onEvent = () => finish();
    const onError = (e: { error?: Error }) => finish(e?.error ?? new Error('The map could not be loaded'));

    function finish(error?: Error): void {
      clearTimeout(timer);
      map.off(event, onEvent);
      map.off('error', onError);
      error ? reject(error) : resolve();
    }

    map.on(event, onEvent);
    map.on('error', onError);
  });
}

/**
 * Copies the map to a picture of the size the report expects, whatever the screen's pixel density is, with the credit
 * for the imagery in the bottom right corner.
 */
function toDataUrl(source: HTMLCanvasElement): string {
  const target = document.createElement('canvas');
  target.width = IMAGE_WIDTH;
  target.height = IMAGE_HEIGHT;

  const context = target.getContext('2d')!;
  context.drawImage(source, 0, 0, IMAGE_WIDTH, IMAGE_HEIGHT);

  context.font = '20px sans-serif';
  context.textAlign = 'right';
  context.textBaseline = 'bottom';
  context.fillStyle = '#FFFFFF';
  context.shadowColor = 'rgba(0, 0, 0, 0.9)';
  context.shadowBlur = 4;
  context.fillText(ATTRIBUTION, IMAGE_WIDTH - 12, IMAGE_HEIGHT - 10);

  return target.toDataURL('image/jpeg', JPEG_QUALITY);
}
